import { Suspense, lazy } from "react";

import { useTranslation } from "react-i18next";
import { Route, Routes, useLocation } from "react-router-dom";

import { Container, Loader, Message } from "semantic-ui-react";
import styled from "styled-components";
import { useTheme } from "styled-components";

import ErrorBoundary from "simplydo/react/components/Error/ErrorBoundary";

import { useAppSelector } from "store";
import util from "utils/utils";

import { Wrap } from "components/AppOnboarding";
import AuthoriseToken from "components/entry/AuthoriseToken";
import LoginError from "components/entry/LoginError";
import LoginWithToken from "components/entry/LoginWithToken";
import SmartLink from "components/entry/SmartLink";
import IdeaPDF, { MultiIdeaPDF } from "components/ideas/IdeaPDF";
import Innointel2CompanyPDF from "components/innointel2/CompanyPDF";
import CompanyPDF from "components/innovationintelligence/Companies/CompanyPDF";
import ReportContent from "components/lib/ReportContent/ReportContent";
import { Footer, NavBar } from "components/lib/UI";

import NonUserSwitch from "./NonUserSwitch";
import SuspenseLoading from "./SuspenseLoading";
import UserSwitch from "./UserSwitch";

const Poll = lazy(() => import("components/polls/Poll"));
const MobileNewIdeaButton = lazy(
  () => import("components/lib/MobileNewIdeaButton"),
);

export const StyledDashboardContainer = styled.div`
  display: flex;
  min-height: 100%;
  flex-direction: column;
`;

export const StyledDashboardContent = styled.div.attrs({
  className: "app-content",
})`
  flex: 1;
  position: relative;
  background-color: #f9f9f9;
  display: flex;
  flex-direction: column;
`;

const AppRouting = () => {
  const theme = useTheme();
  const location = useLocation();
  const { t } = useTranslation();

  const user = useAppSelector((state) => state.user);
  const isGettingUser = useAppSelector((state) => state.auth.isGettingUser);
  const gettingUserError = useAppSelector((state) => state.auth.errorMessage);
  const showLogin = useAppSelector((state) => state.auth.showLogin);
  const ipBlocked = useAppSelector((state) => state.auth.ipBlocked);

  return (
    <Routes>
      <Route
        path="/report"
        element={
          <Wrap
            excludeWelcomeText
            scrolling={false}
            fluid
            content={<ReportContent />}
          />
        }
      />
      <Route
        path="/login/error"
        element={<Wrap excludeWelcomeText content={<LoginError />} />}
      />
      <Route
        path="/login/with"
        element={<Wrap excludeWelcomeText content={<LoginWithToken />} />}
      />
      <Route
        path="/start"
        element={<Wrap excludeWelcomeText content={<SmartLink />} />}
      />
      <Route
        path="/tokens/authorise"
        element={<Wrap excludeWelcomeText content={<AuthoriseToken />} />}
      />
      <Route
        path="/polls/:id"
        element={
          <Wrap scrolling={false} excludeWelcomeText fluid content={<Poll />} />
        }
      />
      <Route
        path="/companies/:id/pdf"
        element={
          <Wrap
            excludeWelcomeText
            fluid
            scrolling={false}
            content={<CompanyPDF />}
          />
        }
      />
      <Route
        path="/innointel2/companies/:companyId/pdf"
        element={<Innointel2CompanyPDF />}
      />
      <Route path="/ideas/multi-pdf/:ids" element={<MultiIdeaPDF />} />
      <Route path="/ideas/:id/pdf" element={<IdeaPDF />} />
      <Route
        path="*"
        element={
          isGettingUser ?
            <Loader active inline="centered" style={{ marginTop: 30 }} />
          : <StyledDashboardContainer>
              <NavBar />
              <ErrorBoundary pathName={location.pathname}>
                <StyledDashboardContent>
                  {ipBlocked ?
                    <Container style={{ marginTop: 20 }}>
                      <Message>
                        <p>
                          <strong>Access denied</strong>
                        </p>
                        <p>
                          The network or location you are trying to access{" "}
                          {util.appName()} from is not permitted in your
                          account&apos;s security settings. Please reach out to
                          an administrator in your organisation for assistance.
                        </p>
                      </Message>
                    </Container>
                  : <Suspense fallback={<SuspenseLoading />}>
                      {user && <UserSwitch />}
                      <NonUserSwitch />
                      {gettingUserError && !showLogin ?
                        <Container style={{ marginTop: 20 }}>
                          <Message>
                            <p>
                              <strong>{t("main.problemLoading")}</strong>
                            </p>
                            <p>{gettingUserError}</p>
                          </Message>
                        </Container>
                      : null}
                      {theme.sizes.isMobile && user && <MobileNewIdeaButton />}
                    </Suspense>
                  }
                </StyledDashboardContent>
              </ErrorBoundary>
              <Footer />
            </StyledDashboardContainer>
        }
      />
    </Routes>
  );
};

export default AppRouting;
